import { useState } from "react";
import { useSelector } from "react-redux";
import { Popconfirm } from "antd";
import { toast, Bounce } from "react-toastify";

import { updateResginationApi } from "api/services/resignations";

const TOAST = { position: "top-right", autoClose: 4000, transition: Bounce };

const DECISIONS = [
  { value: "approve", label: "Approve", icon: "fa fa-check", tone: "approve" },
  { value: "reject", label: "Reject", icon: "fa fa-times", tone: "reject" },
];

const errorMessage = (e, fallback) => e?.response?.data?.error || e?.response?.data?.message || fallback;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const useResignationAction = (resignation, onDone) => {
  const [submitting, setSubmitting] = useState(false);
  const run = (payload, successMessage) => {
    setSubmitting(true);
    return updateResginationApi(resignation.id, { company_user: resignation.company_user?.id, ...payload })
      .then(() => {
        toast.success(successMessage, TOAST);
        onDone?.();
      })
      .catch((e) => toast.error(errorMessage(e, "Could not update the resignation."), TOAST))
      .finally(() => setSubmitting(false));
  };
  return { submitting, run };
};

const DecisionForm = ({ resignation, onDone }) => {
  const [decision, setDecision] = useState(null);
  const [comment, setComment] = useState("");
  const { submitting, run } = useResignationAction(resignation, onDone);

  const submit = (e) => {
    e.preventDefault();
    if (!decision) return;
    run({ action: decision, comment }, decision === "approve" ? "Resignation approved." : "Resignation rejected.");
  };

  return (
    <form className="resignation-actions" onSubmit={submit}>
      <div className="resignation-section__label" id="resignation-decision-label">Your decision</div>
      {/* Toggle buttons, not role="radio": a radiogroup obliges an arrow-key
          contract these plain buttons do not implement (see ListShell.jsx). */}
      <div className="resignation-decision" role="group" aria-labelledby="resignation-decision-label">
        {DECISIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={decision === option.value}
            className={`resignation-decision__option is-${option.tone}`}
            onClick={() => setDecision(option.value)}
          >
            <i className={option.icon} aria-hidden="true" /> {option.label}
          </button>
        ))}
      </div>
      <label className="resignation-section__label mt-3" htmlFor="resignation-comment">
        Comment <span className="text-muted fw-normal">(optional, shown in the approval history)</span>
      </label>
      <textarea
        id="resignation-comment"
        className="form-control"
        rows={3}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="d-flex justify-content-end mt-3">
        <button type="submit" className="btn btn-primary" disabled={!decision || submitting}>
          {submitting && <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />}
          {decision === "reject" ? "Confirm rejection" : "Confirm approval"}
        </button>
      </div>
    </form>
  );
};

const WithdrawAction = ({ resignation, onDone }) => {
  const { submitting, run } = useResignationAction(resignation, onDone);
  return (
    <div className="resignation-actions d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div className="text-muted small">Changed your mind? You can withdraw while the request is pending.</div>
      <Popconfirm
        title="Withdraw this resignation?"
        description="Approvers will no longer see it as pending."
        okText="Withdraw"
        cancelText="Keep it"
        okButtonProps={{ danger: true }}
        onConfirm={() => run({ action: "cancel" }, "Resignation withdrawn.")}
      >
        <button type="button" className="btn btn-outline-danger btn-sm" disabled={submitting}>
          Withdraw resignation
        </button>
      </Popconfirm>
    </div>
  );
};

/** Only the person the request is waiting on may decide; only the employee
 *  may withdraw, and only while it is pending. Everyone else sees no form. */
const ResignationActions = ({ resignation, onDone }) => {
  const { user } = useSelector((state) => state.user);
  if (resignation?.approval_status !== "pending") return null;
  if (sameId(user?.id, resignation.current_approver?.id)) {
    return <DecisionForm resignation={resignation} onDone={onDone} />;
  }
  if (sameId(user?.id, resignation.company_user?.id)) {
    return <WithdrawAction resignation={resignation} onDone={onDone} />;
  }
  return null;
};

export default ResignationActions;
